import { DEFAULT_LOCALE, type Locale } from './types'

/**
 * A piece of **editorial copy**, written in every language at the point it is written.
 *
 * ## Why this is not a catalogue key
 *
 * Ground rule 8 sends labels, placeholders, empty states and `aria-label`s to
 * `src/i18n/locales/en.json`, and this does not change that. It is the other half of the
 * same rule — the half `CLAUDE.md` names and deliberately leaves out of the catalogues:
 *
 * > Editorial copy — the listings, the member terms, scripture — stays in `src/data/`,
 * > because translating it is a decision about tone and about which named translation of a
 * > verse to use, not a lookup.
 *
 * A key indirects: `t('home.lead')` tells a reader of the page nothing about what the page
 * says, and the words live in a file sorted by key rather than by meaning. That is the
 * right trade for two hundred short labels and the wrong one for a paragraph somebody
 * agonised over, because the two languages then sit in different files and drift with
 * nobody able to see it happen. Here they are adjacent, on the same object, and a reviewer
 * comparing them is reading one screen.
 *
 * ## The compiler is the gate, and it is a stronger one than `audit:i18n`
 *
 * `Record<Locale, string>` is **total**. Adding a locale to `LOCALES` turns every piece of
 * editorial copy on the platform into a type error naming the file and the field, which is
 * exactly the list somebody needs in order to translate it. `audit:i18n` cannot do that for
 * the catalogues — it allows an empty file, because a language nobody has started is an
 * honest state — so the two halves are gated differently on purpose.
 *
 * ## A plain string is still allowed, and is not a loophole
 *
 * `text()` takes `Text | string`, because some copy is genuinely the same in every
 * language and writing it twice invites the two copies to diverge under a later edit: a
 * business name, an email address, a telephone number, a URL, a price. **Use a bare string
 * only when translating it would be wrong**, never because the Mongolian has not been
 * written yet — that case is a `Text` whose `mn` is the English, which is visible in review
 * and greppable, where a bare string is neither.
 */
export type Text = Readonly<Record<Locale, string>>

/**
 * One language out of a `Text`.
 *
 * Pure, and free of React and the DOM for the same reason `types.ts` is: `app_mobile` will
 * render the same data modules through the same helper.
 *
 * Falls back to English on a blank, so a `Text` whose Mongolian is an empty string shows
 * the English rather than a gap. A missing *key* cannot happen — the type is total — so
 * this guards only the value.
 */
export const text = (value: Text | string, locale: Locale): string => {
  if (typeof value === 'string') return value
  return value[locale] || value[DEFAULT_LOCALE]
}

/** True for a `Text` rather than a plain string. Narrows, for a caller holding either. */
export const isText = (value: Text | string): value is Text => typeof value !== 'string'
